import { useMemo } from 'react';
import { css } from '@emotion/react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { IconName } from '@frontend/icons';
import { getFullName } from '@frontend/name';
import { SettingsRoutes, useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import { useSchedulingLocationInfo } from '../../../hooks';
import { CalendarEntityType } from '../../../stores';
import { getMaxProviderCount, PROVIDER_LANE_WIDTH } from '../constants';
import { ColumnHeader } from './column-header';
import { LocationBar } from './location-bar';

export interface ColumnEntity {
  id: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  avatarVariant: 'square' | 'circle';
  avatarSrc?: string;
  iconName?: IconName;
  iconColor?: string;
  isArchived?: boolean;
  eventCount?: number;
}
export interface LocationWithCalendarColumnEntities {
  id: string;
  name: string;
  timezone: string;
  columnEntities: ColumnEntity[];
}

interface LocationListWithColumnEntitiesProps {
  calendarEntityType: CalendarEntityType;
  list: LocationWithCalendarColumnEntities[];
  maxWidth: number;
}

export const LocationListWithColumnEntities = ({
  calendarEntityType,
  list,
  maxWidth,
}: LocationListWithColumnEntitiesProps) => {
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const { isMultiLocation } = useSchedulingLocationInfo();
  const navigate = useSettingsNavigate();

  const { gridTemplateColumns, showLocationBar, totalColumnEntities } = useMemo(() => {
    const hasListItems = list.length > 1;
    const totalColEntities = list.reduce((acc, location) => acc + location.columnEntities?.length, 0);
    const gridTemplateCols = list
      .map((item) => (item.columnEntities?.length ? `${item.columnEntities?.length}fr` : ''))
      .join(' ');

    return {
      gridTemplateColumns: gridTemplateCols,
      showLocationBar: hasListItems,
      totalColumnEntities: totalColEntities,
    };
  }, [list]);

  const handleColumnHeaderClick = (locationId: string, entityId: string) => {
    if (!locationId || !entityId) return;

    let route: SettingsRoutes;
    let params;

    if (calendarEntityType === 'provider') {
      route = isMultiLocation
        ? '/schedule/manage-providers/:locationId/edit/:providerId'
        : '/schedule/manage-providers/edit/:providerId';
      params = isMultiLocation ? { locationId, providerId: entityId } : { providerId: entityId };
    } else {
      route = isMultiLocation
        ? '/schedule/operatories/:locationId/edit/:operatoryId'
        : '/schedule/operatories/edit/:operatoryId';
      params = isMultiLocation ? { locationId, operatoryId: entityId } : { operatoryId: entityId };
    }

    navigate.navigate({ to: route, params });
  };

  const shouldSetMaxWidth = getMaxProviderCount(maxWidth) < totalColumnEntities;

  return (
    <section css={styles.mainContainer(gridTemplateColumns)}>
      {list.map((location, index) => (
        <div key={index} css={index !== 0 && styles.itemSeparator}>
          {showLocationBar && (
            <LocationBar
              name={location.name}
              timezone={location.timezone}
              maxWidth={maxWidth}
              columnEntityCount={location.columnEntities?.length}
            />
          )}
          <div css={styles.listContainer}>
            {location.columnEntities?.map((columnEntity) => (
              <ColumnHeader
                key={columnEntity.id}
                id={columnEntity.id}
                firstName={columnEntity.firstName}
                lastName={columnEntity.lastName}
                name={getFullName(columnEntity)}
                avatarSrc={columnEntity.avatarSrc}
                iconName={columnEntity.iconName}
                iconColor={columnEntity.iconColor}
                isArchived={columnEntity.isArchived}
                eventCount={columnEntity.eventCount}
                avatarVariant={calendarEntityType === 'provider' ? 'circle' : 'square'}
                shouldSetMaxWidth={shouldSetMaxWidth}
                onClick={
                  isScheduleV3FlagEnabled ? () => handleColumnHeaderClick(location.id, columnEntity.id) : undefined
                }
              />
            ))}
          </div>
        </div>
      ))}
    </section>
  );
};

const styles = {
  mainContainer: (gridTemplateColumns: string) =>
    css({
      display: 'grid',
      gridTemplateColumns: gridTemplateColumns,
      gridTemplateRows: 'auto',
      width: '100%',
      gap: theme.spacing(2),
      marginRight: theme.spacing(2),
    }),
  itemSeparator: css({
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      width: 1,
      height: '100%',
      backgroundColor: theme.colors.neutral10,
      top: 0,
      left: theme.spacing(-1),
    },
  }),
  listContainer: css({
    display: 'flex',
    height: 36,
    '> *': {
      flex: `1 0 ${PROVIDER_LANE_WIDTH}px`,
      justifyContent: 'center',
      '&:not(:last-child)': {
        boxShadow: `1px 0 0 ${theme.colors.neutral10}`,
      },
    },
  }),
};
